// Inlined here (instead of importing from "@/components/SEO") so this
// module stays free of React/Helmet deps and can be loaded by the
// Node-side prerender script.
const createFAQSchema = (faqs: { question: string; answer: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: { "@type": "Answer", text: faq.answer },
  }),
  ),
});

// FAQ data for each insight page - optimized for GEO (LLMs love Q&A format)

export const summerEmailFAQs = [
  {
    question: "Why do email reply rates drop in summer?",
    answer: "Summer reply rates soften because decision makers and subscribers are on holiday or working with reduced availability between mid-June and late August. Industry coverage from HubSpot, Seguno and Demand Gen Report consistently describes July as a calendar lull and August as the deepest B2B trough, especially in Europe. We avoid quoting a precise open-rate delta because Apple Mail Privacy Protection (2021) inflates opens and makes month-over-month comparisons unreliable, and we don't quote a 'time to first reply' figure because there is no reliable industry benchmark for it. This is seasonal behaviour, not a deliverability failure."
  },
  {
    question: "Is my email deliverability broken in summer?",
    answer: "No. Independent inbox placement benchmarks do not show a summer spike in spam-folder rates. Spam complaint rates, bounce rates and authentication (SPF/DKIM/DMARC) pass rates do not materially change with the season. If your deliverability indicators are healthy, the summer drop in replies is recipient attention — vacations, reduced hours and out-of-office coverage — not emails going to spam."
  },
  {
    question: "When do email reply rates return to normal after summer?",
    answer: "Email reply rates typically begin recovering in the first full week of September and return to normal levels by mid-September. In Europe, where August is the peak holiday month, recovery may take until late September. The post-summer period (September-October) is often one of the best windows for B2B outreach as inboxes are less crowded than in Q4."
  },
  {
    question: "Should I pause email campaigns in summer?",
    answer: "Not necessarily. While B2B response rates are lower, some campaigns still convert — especially async-friendly formats like digest roundups, resource downloads, and evergreen guides. Consider reducing frequency by 20-30%, avoiding major product launches in July-August, and scheduling your strongest campaigns for September."
  },
  {
    question: "Which industries are hit hardest by the summer email dip?",
    answer: "B2B SaaS, enterprise software, professional services and consulting feel the slowdown most because the buyers themselves are on leave. Ecommerce and travel brands often see summer engagement hold steady or even rise on the back of holiday bookings and summer sales. B2C newsletters tend to see a mild general dip. The exact magnitude varies a lot by dataset, so we describe direction rather than precise percentages."
  },
  {
    question: "How does the summer dip differ between the US and Europe?",
    answer: "The US sees a shorter, shallower dip concentrated around Independence Day (July 4) and scattered PTO weeks, with many workers still partially reachable. Europe — especially France, Italy, Spain and Germany — has a strong August shutdown culture where entire offices close for 2–4 weeks and decision makers are genuinely unreachable. Qualitatively this makes August the deepest B2B trough in Europe, but precise country-level open-rate percentages are not well sourced, so we don't quote them."
  },
  {
    question: "What is the best time to send emails during summer?",
    answer: "Tuesday through Thursday, mid-morning in the recipient's timezone, remains the general best-practice window even in summer, but absolute engagement is lower across all send times. Avoid Monday mornings (catch-up backlog) and Friday afternoons (early weekend departures). The week of July 4 in the US and most of August for heavily European B2B lists should be avoided for important campaigns."
  },
  {
    question: "How should I adjust my email strategy for summer?",
    answer: "Practical adjustments: (1) reduce campaign frequency by 20-30%, (2) move major launches to September-October, (3) lean into async content that ages well (guides, reports, case studies), (4) extend offer deadlines to account for delayed replies, (5) use digest-style formats people can scan when back, (6) set nurture sequences to resume in September, (7) do not panic about lower metrics — compare against seasonal benchmarks, not spring numbers."
  }
];

export const decemberEmailFAQs = [
  {
    question: "Why do email reply rates drop in December?",
    answer: "December email reply rates drop due to holiday seasonality, not deliverability issues. People are on vacation, inboxes are flooded with promotional emails (30%+ increase), and attention is focused on family rather than business. Conversion rates typically drop from 6.1% to 2.9% during this period."
  },
  {
    question: "Is my email deliverability broken in December?",
    answer: "No, December email performance drops are almost always seasonal, not technical. Global inbox placement rates remain stable at around 84%. The drop in responses is due to recipient behavior (vacations, holiday distractions) rather than emails going to spam."
  },
  {
    question: "When do email reply rates return to normal after holidays?",
    answer: "Email reply rates typically begin recovering in the second week of January and return to normal levels by late January or early February. The first week of January often has lower-than-usual engagement as people catch up on holiday backlogs."
  },
  {
    question: "Should I pause email campaigns in December?",
    answer: "Not necessarily. While response rates are lower, some industries see success with holiday-themed campaigns. Consider reducing volume, adjusting expectations, and focusing on relationship-building rather than hard sales during the holiday period."
  }
];

export const spfDkimDmarcFAQs = [
  {
    question: "What is SPF and how does it work?",
    answer: "SPF (Sender Policy Framework) is an email authentication protocol that specifies which mail servers are authorized to send email for your domain. It works by publishing a DNS TXT record listing approved sending IP addresses. Example: v=spf1 include:_spf.google.com ~all"
  },
  {
    question: "What is DKIM and why is it important?",
    answer: "DKIM (DomainKeys Identified Mail) adds a digital signature to outgoing emails that verifies the content hasn't been altered and confirms the sender's domain identity. It's crucial for email deliverability because it proves email authenticity to receiving servers."
  },
  {
    question: "What does DMARC do?",
    answer: "DMARC (Domain-based Message Authentication, Reporting & Conformance) tells receiving servers what to do with emails that fail SPF or DKIM checks. Options include 'none' (monitor only), 'quarantine' (send to spam), or 'reject' (block entirely). It also provides reporting on authentication failures."
  },
  {
    question: "Do I need all three: SPF, DKIM, and DMARC?",
    answer: "Yes, you should implement all three protocols. SPF authorizes sending servers, DKIM verifies message integrity, and DMARC provides policy enforcement and reporting. Together, they form a complete email authentication system that maximizes deliverability and prevents spoofing."
  },
  {
    question: "How do I check if my SPF, DKIM, and DMARC are set up correctly?",
    answer: "Use an email authentication checker tool to verify your DNS records. Check that your SPF record includes all legitimate sending servers, DKIM signatures are valid, and DMARC policy is published. Send test emails and analyze headers to confirm authentication passes."
  }
];

export const coldEmailFAQs = [
  {
    question: "What is a good cold email open rate?",
    answer: "A good cold email open rate is 40-60%. Top performers achieve 60-80%. However, open rates are increasingly unreliable due to Apple Mail Privacy Protection and security bots. Click-through rate and reply rate are more meaningful metrics."
  },
  {
    question: "How many cold emails can I send per day?",
    answer: "For new domains, start with 20-30 emails per day and gradually increase over 4-8 weeks. Established domains with good reputation can send 50-100 per day. Sending too many too fast damages sender reputation and triggers spam filters."
  },
  {
    question: "Why are my cold emails going to spam?",
    answer: "Cold emails land in spam due to: missing email authentication (SPF, DKIM, DMARC), poor sender reputation, spam trigger words, sending too many emails too fast, high bounce rates, or lack of personalization. Check your authentication and warm up your domain properly."
  },
  {
    question: "What is the 0.3% spam complaint threshold?",
    answer: "Major email providers like Google flag senders who exceed 0.3% spam complaint rate (3 complaints per 1,000 emails). Consistently exceeding this threshold leads to blocking or severe filtering. Monitor complaint rates and remove unengaged recipients."
  }
];

export const gmailPromotionsFAQs = [
  {
    question: "Why do my emails go to Gmail Promotions tab?",
    answer: "Gmail uses machine learning to categorize emails. Emails land in Promotions due to: marketing-style content, HTML-heavy templates, promotional language, unsubscribe links, commercial sender patterns, and lack of personal engagement history with the recipient."
  },
  {
    question: "How do I get emails into Gmail Primary tab?",
    answer: "To reach Gmail's Primary tab: use plain text or minimal HTML, write conversationally, avoid promotional language, personalize content, build engagement history, use a personal sender name, and ensure recipients reply to your emails (signals importance)."
  },
  {
    question: "Do people check the Gmail Promotions tab?",
    answer: "Yes, 68.8% of Gmail users check their Promotions tab, and the average open rate is 19.2%. While lower than Primary, Promotions tab emails still get significant engagement. Focus on compelling subject lines and sender recognition."
  }
];

export const vibeCodingFAQs = [
  {
    question: "What is vibe coding?",
    answer: "Vibe coding is an AI-assisted development approach where developers describe what they want in natural language, and AI coding assistants generate the code. It emphasizes intent over syntax, allowing 10x faster prototyping. Popular tools include Cursor, Bolt, and Claude."
  },
  {
    question: "What are the best AI coding assistants in 2025?",
    answer: "Top AI coding assistants in 2025 include: Cursor (best for full IDE integration), GitHub Copilot (best for code completion), Claude (best for complex reasoning), ChatGPT/GPT-4 (most versatile), and Windsurf (best for full-stack AI development)."
  },
  {
    question: "How do I write better prompts for AI coding?",
    answer: "Write better AI coding prompts by: being specific about requirements, providing context and constraints, including examples of desired output, breaking complex tasks into steps, specifying the tech stack, and iterating based on results."
  }
];

export const phishingFAQs = [
  {
    question: "How do I identify a phishing email?",
    answer: "Identify phishing emails by checking: sender email domain (not display name), urgent or threatening language, requests for credentials or money, suspicious links (hover to preview), poor grammar, generic greetings, and mismatched branding or logos."
  },
  {
    question: "What percentage of cyberattacks start with phishing?",
    answer: "91% of cyberattacks begin with a phishing email. Phishing is the most common attack vector because it exploits human psychology rather than technical vulnerabilities. The average cost of a data breach is $4.76 million."
  },
  {
    question: "What should I do if I clicked a phishing link?",
    answer: "If you clicked a phishing link: immediately change passwords for any accounts you entered credentials for, enable 2FA, scan your device for malware, notify your IT/security team, monitor accounts for suspicious activity, and report the phishing email."
  }
];

export const pmToolsFAQs = [
  {
    question: "What is the best project management tool for small teams in 2025?",
    answer: "Lottae is the best project management tool for small teams in 2025, offering the lowest per-user cost at $3.50/user with no feature gating. Unlike Asana ($10.99+/user), Monday.com ($9+/user), or Trello, Lottae provides unlimited customization and white-labeling at the base price, making it ideal for budget-conscious teams."
  },
  {
    question: "How much does Lottae project management software cost?",
    answer: "Lottae costs $7/month per workspace plus $3.50 per user (minimum 5 users). A 10-user team costs approximately $51/month, a 25-user team costs $110/month, and a 60-user team costs $242/month. All users get full access to every feature including Kanban, Timeline, Custom Workflows, and Dashboards with no tier restrictions."
  },
  {
    question: "What makes Lottae different from other project management tools?",
    answer: "Lottae differentiates itself with three key advantages: 1) Lowest per-user pricing at $3.50/user vs $10-15 industry average, 2) No feature gating — every user gets Kanban, Timeline, Custom Workflows, and Dashboards, 3) White-labeling and custom domain support included at base price, not locked behind enterprise tiers like Asana or Monday.com."
  },
  {
    question: "Which project management tool offers the best customization?",
    answer: "Lottae offers the best customization among project management tools for the price. While Jira and ClickUp offer high customization, they lock advanced features behind expensive enterprise plans. Lottae provides unlimited customization, custom workflows, role-based permissions, and white-labeling to all users regardless of team size."
  },
  {
    question: "How does Lottae compare to Asana, Monday.com, and Trello?",
    answer: "Lottae costs $3.50/user vs Asana ($10.99+), Monday.com ($9+), and Trello ($5+). Lottae includes white-labeling and custom domains at the base price, while competitors require enterprise plans costing 3-5x more. Lottae offers unlimited customization while Trello and Asana have limited options on non-enterprise tiers."
  }
];

export const domainWarmupFAQs = [
  {
    question: "What is domain warmup and why is it important?",
    answer: "Domain warmup is the process of gradually building sender reputation for a new domain by slowly increasing email volume over time. It's essential because ISPs are suspicious of new domains sending high volumes, which can trigger spam filters."
  },
  {
    question: "How long does domain warmup take?",
    answer: "A proper domain warmup takes 4-8 weeks minimum. Start with 10-20 emails per day in week one, gradually increasing to your target volume. Rushing this process risks damaging your sender reputation permanently."
  },
  {
    question: "Can I speed up the domain warmup process?",
    answer: "While you can't safely skip warmup, you can optimize it by: sending to highly engaged contacts first, ensuring every email gets opens and replies, using warmup services carefully, and maintaining perfect technical setup from day one."
  },
  {
    question: "What happens if I skip domain warmup?",
    answer: "Skipping warmup typically results in emails going directly to spam, potential blacklisting, permanent reputation damage that's difficult to recover from, and wasted resources on emails that never reach recipients."
  }
];

export const sendingTimeFAQs = [
  {
    question: "What is the best time to send cold emails?",
    answer: "Research shows Tuesday through Thursday between 9-11 AM in the recipient's local timezone yields the highest engagement. However, testing with your specific audience is essential as optimal times vary by industry and recipient behavior."
  },
  {
    question: "Does sending time affect spam filtering?",
    answer: "Yes, sending patterns influence spam detection. Sudden spikes in volume, sending at unusual hours, or patterns that look automated can trigger spam filters. Consistent, human-like sending patterns during business hours perform better."
  },
  {
    question: "Should I send all my emails at once or spread them out?",
    answer: "Always spread emails throughout the day to mimic natural sending behavior. Batch sending looks automated and can trigger spam filters. Use sending limits of 50-100 emails per hour maximum for cold outreach."
  },
  {
    question: "Does the day of the week affect email deliverability?",
    answer: "Day of week primarily affects engagement rather than deliverability. However, since engagement impacts sender reputation, sending on high-engagement days (Tuesday-Thursday) indirectly supports better long-term deliverability."
  }
];

export const apiSecurityFAQs = [
  {
    question: "What are the most critical API security vulnerabilities?",
    answer: "The top vulnerabilities are: broken authentication, broken object-level authorization, excessive data exposure, lack of rate limiting, injection attacks, and insufficient logging. OWASP API Security Top 10 provides comprehensive coverage."
  },
  {
    question: "How should I authenticate API requests?",
    answer: "Use OAuth 2.0 or JWT tokens for authentication, never API keys in URLs. Implement token expiration, refresh token rotation, and consider mutual TLS for high-security APIs. Always use HTTPS and validate tokens server-side."
  },
  {
    question: "What is rate limiting and why is it important for APIs?",
    answer: "Rate limiting restricts the number of API requests a client can make within a time window. It prevents abuse, DDoS attacks, brute force attempts, and ensures fair resource usage. Implement at both application and infrastructure levels."
  },
  {
    question: "How do I secure sensitive data in API responses?",
    answer: "Never expose more data than necessary (principle of least privilege), use field-level filtering, encrypt sensitive fields, implement proper access controls, mask PII in logs, and use separate endpoints for sensitive operations."
  }
];

export const aiCodingFAQs = [
  {
    question: "Which AI coding assistant is best for beginners?",
    answer: "GitHub Copilot is ideal for beginners due to its seamless IDE integration, inline suggestions that teach patterns, and extensive training data. Cursor offers a more guided experience with chat-based interaction that explains code as it writes."
  },
  {
    question: "Can AI coding assistants replace human developers?",
    answer: "No, AI assistants augment rather than replace developers. They accelerate routine tasks but lack understanding of business context, architectural decisions, and edge cases. Human oversight, code review, and strategic thinking remain essential."
  },
  {
    question: "Are AI coding assistants secure for enterprise use?",
    answer: "Enterprise versions from GitHub, AWS, and others offer private deployments, data privacy guarantees, and compliance certifications. However, always review AI-generated code for security vulnerabilities and avoid sharing sensitive data in prompts."
  },
  {
    question: "How much faster can AI coding assistants make development?",
    answer: "Studies show 30-55% productivity gains for routine tasks, with some developers reporting 2-3x speed improvements for boilerplate code. Complex problem-solving and architecture work see smaller gains as these require human judgment."
  }
];

export const promptEngineeringFAQs = [
  {
    question: "What is prompt engineering for developers?",
    answer: "Prompt engineering is the practice of crafting effective instructions for AI models to generate accurate, useful code. It involves providing context, specifying constraints, giving examples, and iteratively refining prompts based on output quality."
  },
  {
    question: "What makes a good coding prompt?",
    answer: "Effective coding prompts include: clear objective, technology stack, input/output examples, constraints and edge cases, code style preferences, and context about the broader system. Be specific but not overly prescriptive."
  },
  {
    question: "How do I get AI to write secure code?",
    answer: "Explicitly request security considerations in your prompts: 'Implement with input validation, SQL injection prevention, and proper error handling.' Ask for security review of generated code and specify compliance requirements."
  },
  {
    question: "Should I use few-shot or zero-shot prompts for coding?",
    answer: "Few-shot prompts (with examples) work better for consistent formatting, specific patterns, and complex logic. Zero-shot works for standard implementations. Start zero-shot, add examples if output doesn't match expectations."
  }
];

export const noCodeFAQs = [
  {
    question: "When should I use no-code instead of traditional coding?",
    answer: "Use no-code for: MVPs and prototypes, internal tools, simple CRUD apps, marketing sites, and when speed-to-market trumps customization. Choose traditional code for: complex business logic, high-performance needs, unique features, and long-term scalability."
  },
  {
    question: "What are the limitations of no-code platforms?",
    answer: "Common limitations include: vendor lock-in, limited customization, performance constraints, scalability ceilings, integration restrictions, and ongoing subscription costs. Complex applications often outgrow no-code capabilities."
  },
  {
    question: "Can I migrate from no-code to traditional code later?",
    answer: "Migration varies by platform. Some allow code export, others don't. Plan for potential migration by: documenting requirements, using platforms with export options, and designing modular systems where possible."
  },
  {
    question: "Is no-code cheaper than hiring developers?",
    answer: "Initially yes, but costs can exceed traditional development for complex, long-running projects. Calculate: platform fees, integration costs, customization limitations, and potential rebuild costs. No-code excels for simple, short-term projects."
  }
];

// Reply-Based Deliverability FAQs
export const replyDeliverabilityFAQs = [
  {
    question: "Why are email open rates no longer reliable?",
    answer: "Open rates became unreliable due to Apple Mail Privacy Protection (MPP), which pre-fetches images and inflates open rates by 30-40%. Security bots and corporate email scanners also trigger false opens. ISPs now weight reply rates and engagement signals more heavily than opens."
  },
  {
    question: "What is reply-based deliverability?",
    answer: "Reply-based deliverability is the shift by major ISPs (Gmail, Yahoo, Microsoft) to prioritize email replies as the primary engagement signal for inbox placement. Emails that generate genuine replies are more likely to reach the inbox, while low-reply senders face spam filtering."
  },
  {
    question: "What is a good email reply rate for cold outreach?",
    answer: "A good cold email reply rate is 5-10%. Top performers achieve 15-20%+ through personalization and relevance. Reply rates below 2% signal to ISPs that recipients don't want your emails, potentially triggering spam filtering for future sends."
  },
  {
    question: "How do I increase my email reply rate?",
    answer: "Increase reply rates by: asking genuine questions, personalizing beyond {{firstName}}, sending to smaller verified lists, using conversational tone, making replies easy (one-click responses), and timing sends for when recipients are active. Focus on value over volume."
  },
  {
    question: "Do negative replies hurt deliverability?",
    answer: "No, negative replies actually help deliverability. ISPs see any reply as engagement, signaling that your emails are wanted enough to warrant a response. However, spam complaints (clicking 'Report Spam') do hurt deliverability significantly."
  }
];

// Zero-Trust Email DMARC FAQs
export const zeroTrustDmarcFAQs = [
  {
    question: "What is zero-trust email authentication?",
    answer: "Zero-trust email authentication treats every incoming email as suspicious until it proves its identity through strict SPF, DKIM, and DMARC verification. In 2026, major ISPs require this model—emails failing authentication are rejected outright, not just marked as spam."
  },
  {
    question: "What is DMARC strict alignment?",
    answer: "Strict alignment requires the domain in the RFC5322.From header to exactly match the domains authenticated by SPF and DKIM. Relaxed alignment (allowing subdomains) is no longer sufficient for Google and Yahoo's 2026 requirements. Example: mail.example.com won't align with example.com under strict mode."
  },
  {
    question: "Why is p=reject now required for DMARC?",
    answer: "With 95% of phishing using domain spoofing, ISPs no longer trust p=none or p=quarantine policies. p=reject instructs receiving servers to block unauthenticated emails entirely, preventing brand impersonation and protecting recipients from fraud."
  },
  {
    question: "How do I transition from p=none to p=reject?",
    answer: "Transition gradually: 1) Audit all sending services using DMARC reports, 2) Ensure SPF/DKIM alignment for each sender, 3) Move to p=quarantine with a low percentage, 4) Increase percentage over 4-8 weeks while monitoring, 5) Finally switch to p=reject once all legitimate mail passes."
  },
  {
    question: "What happens if my third-party senders aren't DMARC compliant?",
    answer: "Emails from non-compliant third parties will fail authentication and be rejected under p=reject. You must either: get the vendor to implement proper alignment, use a subdomain with separate DNS records, or find an alternative service that supports modern email authentication."
  }
];

// GEO vs SEO FAQs
export const geoVsSeoFAQs = [
  {
    question: "What is GEO (Generative Engine Optimization)?",
    answer: "GEO is the practice of optimizing content to be cited by AI search engines like ChatGPT, Perplexity, and Google AI Overviews. Unlike SEO which targets SERP rankings, GEO focuses on structured data, FAQ formats, and authoritative statements that AI models can easily extract and cite."
  },
  {
    question: "How do I rank in ChatGPT and Perplexity search?",
    answer: "Rank in AI search by: using FAQ and Q&A structured data, writing definitive statements AI can quote, adding Dataset schema for statistics, building topical authority clusters, and ensuring factual accuracy. AI models prioritize citable, well-structured content over keyword-optimized articles."
  },
  {
    question: "Is SEO dead because of AI search?",
    answer: "No, SEO isn't dead but it's evolving. Traditional SEO still matters for search engines, but 40%+ of searches now involve AI. The winning strategy is 'GEO + SEO'—optimizing for both channels. Content that ranks in Google and gets cited by AI captures maximum visibility."
  },
  {
    question: "What structured data helps with GEO?",
    answer: "Key schemas for GEO include: FAQPage for Q&A content, HowTo for tutorials, Dataset for statistics and benchmarks, Article for editorial content, and Organization for brand authority. JSON-LD is the preferred format as AI crawlers parse it most reliably."
  },
  {
    question: "How do I measure GEO success?",
    answer: "Measure GEO by: monitoring brand mentions in AI responses (manually or via tools), tracking referral traffic from ai.perplexity.ai and chatgpt.com, checking if your data appears in AI Overviews, and using citation tracking tools. Unlike SEO, there's no single 'ranking' to track."
  }
];

export const aiAgentsFAQs = [
  {
    question: "What are AI agents and how do they differ from chatbots?",
    answer: "AI agents can autonomously execute multi-step tasks, use tools, and make decisions, while chatbots primarily respond to queries. Agents can browse the web, write files, call APIs, and complete complex workflows without human intervention at each step."
  },
  {
    question: "What tasks can AI agents automate?",
    answer: "AI agents excel at: research and data gathering, content creation workflows, code generation and testing, customer support escalation, data entry and processing, scheduling and coordination, and multi-system integrations."
  },
  {
    question: "Are AI agents reliable enough for production use?",
    answer: "Current agents work well for structured, low-risk tasks with human oversight. For critical operations, implement guardrails, approval workflows, and fallback mechanisms. Reliability improves with clear constraints and well-defined objectives."
  },
  {
    question: "How do I build my first AI agent?",
    answer: "Start with frameworks like LangChain, AutoGPT, or CrewAI. Define a specific, narrow task, create clear tool definitions, implement error handling, add logging for debugging, and iterate based on failure cases."
  }
];

export const llmIntegrationFAQs = [
  {
    question: "Which LLM provider should I use for my application?",
    answer: "OpenAI offers the best general-purpose models, Anthropic excels at safety and long context, Google provides competitive pricing, and open-source models (Llama, Mistral) offer self-hosting options. Choose based on: use case, budget, privacy requirements, and latency needs."
  },
  {
    question: "How do I handle LLM API rate limits in production?",
    answer: "Implement exponential backoff with jitter, use request queuing, cache common responses, batch requests where possible, distribute across multiple API keys, and consider self-hosted models for high-volume use cases."
  },
  {
    question: "What is the cost of integrating LLMs into my application?",
    answer: "Costs depend on model choice, token usage, and request volume. GPT-4 costs $0.03-0.06/1K tokens, GPT-3.5 is 10-20x cheaper, Claude offers competitive rates. Estimate based on average prompt/response lengths and expected usage."
  },
  {
    question: "How do I reduce hallucinations in LLM outputs?",
    answer: "Use RAG (Retrieval Augmented Generation) to ground responses in facts, lower temperature settings, implement fact-checking pipelines, provide explicit context, and use structured output formats that constrain possible responses."
  }
];

export const secureCodingFAQs = [
  {
    question: "What are the most common security vulnerabilities in web applications?",
    answer: "The OWASP Top 10 includes: injection attacks (SQL, XSS), broken authentication, sensitive data exposure, XML external entities, broken access control, security misconfiguration, insecure deserialization, and insufficient logging."
  },
  {
    question: "How do I prevent SQL injection attacks?",
    answer: "Use parameterized queries or prepared statements, never concatenate user input into SQL, implement input validation, use ORM frameworks properly, apply principle of least privilege to database users, and regularly test with security scanners."
  },
  {
    question: "What is the principle of least privilege in secure coding?",
    answer: "Grant only the minimum permissions necessary for a function to operate. This applies to: database users, API tokens, file system access, network permissions, and user roles. Limits damage from compromised components."
  },
  {
    question: "How often should I update dependencies for security?",
    answer: "Monitor dependencies continuously with tools like Dependabot or Snyk. Apply critical security patches immediately, review and update monthly, and maintain a dependency inventory. Outdated dependencies are a leading cause of breaches."
  }
];

// Type for FAQ items
export interface InsightFAQ {
  question: string;
  answer: string;
}

export const emailBlacklistsFAQs = [
  {
    question: "Does a SURBL listing affect Gmail delivery?",
    answer: "No. Gmail does not consult public blacklists for inbox placement, it uses its own internal reputation systems built on engagement and complaint data. SURBL affects recipients sitting behind corporate gateway filters such as SpamAssassin based systems, Proofpoint, Mimecast and Barracuda."
  },
  {
    question: "Can a domain get blacklisted without sending any email?",
    answer: "Yes. Blacklists ingest feeds of newly registered domains and preemptively list clusters that match known spam infrastructure patterns, such as many fresh domains sharing one brand root with common modifiers, registrations clustered close together, very young domain age, and certain top level domains. These predictive listings can occur before a single message is sent."
  },
  {
    question: "Do blacklist listings expire?",
    answer: "Many do. Listings on automated lists commonly age out on their own once the associated activity stops. The Spamhaus DBL is highly automated in this way, so a listing tied to activity that has ended will often drop off without any manual request."
  },
  {
    question: "Should I check blacklists regularly?",
    answer: "Monitoring is useful as an early warning signal, but the alert is not the outcome. Interpret every listing by severity, meaning which list it is and who actually queries it, and by measured impact on inbox placement and reply rates. A listing with no measurable effect on delivery is information, not an incident."
  },
  {
    question: "What is a spam trap?",
    answer: "A spam trap is a fake address maintained by blacklist operators that no real person owns, so it can never opt in to receive mail. Sending to one proves the address list was scraped or purchased rather than earned, which is why trap hits are treated as strong evidence of poor list sourcing."
  }
];

// Helper to get FAQs for an insight page
export const getInsightFAQs = (slug: string): InsightFAQ[] => {
const faqMap: Record<string, InsightFAQ[]> = {
    'email-blacklists-explained': emailBlacklistsFAQs,
    'summer-email-drop': summerEmailFAQs,
    'december-email-drop': decemberEmailFAQs,
    'spf-dkim-dmarc-guide': spfDkimDmarcFAQs,
    'cold-email-deliverability': coldEmailFAQs,
    'gmail-promotions-tab': gmailPromotionsFAQs,
    'vibe-coding-guide': vibeCodingFAQs,
    'phishing-attack-detection': phishingFAQs,
    'project-management-tools': pmToolsFAQs,
    'domain-warmup-strategies': domainWarmupFAQs,
    'sending-time-spam-filters': sendingTimeFAQs,
    'api-security-best-practices': apiSecurityFAQs,
    'ai-coding-assistants': aiCodingFAQs,
    'prompt-engineering-developers': promptEngineeringFAQs,
    'no-code-vs-code': noCodeFAQs,
    'ai-agents-automation': aiAgentsFAQs,
    'llm-integration-guide': llmIntegrationFAQs,
    'secure-coding-practices': secureCodingFAQs,
    'reply-based-deliverability': replyDeliverabilityFAQs,
    'zero-trust-email-dmarc': zeroTrustDmarcFAQs,
    'geo-vs-seo': geoVsSeoFAQs,
    'it-stakeholder-roles': itStakeholderFAQs,
    'startup-failure-delegation': startupFailureFAQs,
    'departmental-autonomy': departmentalAutonomyFAQs,
    'upwork-fiverr-decline': upworkFiverrDeclineFAQs,
    'microsoft-drops-claude-code': microsoftClaudeCodeFAQs,
    'ai-cost-vs-employees': aiCostVsEmployeesFAQs,
  };
  
  return faqMap[slug] || [];
};

// Upwork, Fiverr & Freelancer.com Decline FAQs
export const upworkFiverrDeclineFAQs = [
  {
    question: "How many clients has Upwork lost?",
    answer: "Upwork ended 2025 with approximately 785,000 active clients, down from ~832,000 in 2024 — a loss of roughly 47,000 active clients in a single year (about -6% YoY). This is the largest contraction in the company's history as a public company."
  },
  {
    question: "Why is Upwork's stock down so much?",
    answer: "Upwork (NASDAQ: UPWK) is down roughly 56% year-to-date in 2026 and about 87% from its July 2021 peak of $60.70 to around $8 (May 2026). The stock dropped ~19% in a single day after Q4 2025 earnings (Feb 10, 2026) on the back of shrinking active clients, weak 2026 guidance, and AI-driven demand erosion in low-end freelance categories."
  },
  {
    question: "Is Fiverr losing users?",
    answer: "Yes. Fiverr (NYSE: FVRR) reported active buyers down 13.6% YoY in Q4 2025. The company's 2026 revenue guidance of $380–420M came in roughly 12% below the ~$456.8M consensus estimate, the stock dropped 13–17% on Feb 18, 2026, and it laid off about 250 employees (~30% of the workforce) in September 2025 while pivoting to an 'AI-first' company."
  },
  {
    question: "Is Freelancer.com also declining?",
    answer: "Yes. Freelancer Limited (ASX: FLN), the parent of Freelancer.com and Escrow.com, reported FY25 group GMV of A$881.5M, down 7.1% YoY. Escrow.com GMV fell 8.6% while the Freelancer.com segment was nearly flat at +2.3% GMV and +0.7% revenue. The stock (FLN.AX) is down about 37% year-to-date in 2026 and dropped 14.7% in the five days after its Q1 2026 update on April 22, 2026."
  },
  {
    question: "Why are freelancers and clients leaving Upwork and Fiverr?",
    answer: "The biggest structural driver is generative AI. Wharton research (Prof. Manav Raj, 2024) shows AI has measurably displaced freelance demand in writing, translation, and basic coding since ChatGPT launched in late 2022. Commodity gigs — blog posts, simple logos, basic code, translations — are increasingly being handled directly by ChatGPT, Claude, and Midjourney instead of going to a freelance marketplace. The same pattern is visible across all three public platforms (Upwork, Fiverr, Freelancer.com)."
  },
  {
    question: "Are Upwork, Fiverr and Freelancer.com going out of business?",
    answer: "None are going out of business in the short term — all three are still profitable (Freelancer Limited just posted its first NPAT-positive year in a while at A$2.2M). But the public markets are pricing in a smaller, structurally weaker future. All three are repositioning around higher-value 'agency' or 'enterprise' work and building their own AI tooling to stay relevant to clients who would otherwise skip the platform entirely."
  },
  {
    question: "What should businesses use instead of Upwork, Fiverr and Freelancer.com?",
    answer: "For commodity tasks, AI tools like ChatGPT, Claude, and Midjourney now handle most writing, translation, and basic design work directly. For complex, ongoing projects, businesses are increasingly hiring vetted agencies, fractional specialists, or building small in-house teams supported by AI — which gives more accountability, IP protection, and quality control than open marketplaces. Private platforms like Toptal (premium talent), Guru and PeoplePerHour still exist but show similar AI-driven pressure on commodity gigs."
  }
];

// Chain of Command / Skip-Level FAQs
export const departmentalAutonomyFAQs = [
  {
    question: "What is skip-level communication?",
    answer: "Skip-level communication is when a leader communicates directly with employees who don't report to them, bypassing the manager in between. For example, a CEO messaging a developer directly instead of going through the Engineering Manager."
  },
  {
    question: "Why is skipping levels in the chain of command a problem?",
    answer: "Skipping levels undermines manager authority, creates conflicting priorities, leaves managers without context about their team's work, erodes trust in middle management, and trains employees to bypass the chain themselves. It leads to 45% higher manager turnover."
  },
  {
    question: "How should executives communicate with individual contributors?",
    answer: "Executives should always go through the manager first. Whether it's assigning tasks, giving feedback, or asking for status updates—tell the manager, and let the manager handle it. The only exceptions are scheduled skip-level 1:1s, ethics/HR issues, or true emergencies."
  },
  {
    question: "What happens when CEOs give tasks directly to employees?",
    answer: "When CEOs assign tasks directly to ICs, it signals the manager doesn't matter, creates conflicting priorities (manager vs CEO expectations), leaves the manager blind to their team's workload, and trains everyone to wait for 'the real boss' instead of respecting the chain."
  },
  {
    question: "When is skip-level communication appropriate?",
    answer: "Skip-level is appropriate for: scheduled skip-level 1:1s (transparent and known to managers), ethics/HR issues that can't go through normal channels, concerns about the manager themselves, and genuine emergencies where time is critical (but still loop in the manager ASAP)."
  },
  {
    question: "How do I respect the chain of command as a leader?",
    answer: "Before contacting anyone, ask 'Who is their manager?' and go to that manager first. Share feedback about ICs with their manager. Let managers prioritize and assign work. Don't give direction to skip-levels in public channels. Trust managers to handle things their way."
  }
];

// Startup Failure & Delegation FAQs
export const startupFailureFAQs = [
  {
    question: "Why do startups fail?",
    answer: "According to CB Insights data, the top reasons startups fail are: running out of cash (38%), no market need (35%), getting outcompeted (20%), flawed business model (19%), and team problems (23%). Many of these trace back to founder bandwidth issues and delegation failures."
  },
  {
    question: "What is Founder Syndrome?",
    answer: "Founder Syndrome is a pattern where founders cannot or will not delegate responsibilities, leading to micromanagement, team turnover, burnout, and growth bottlenecks. It occurs when founders believe only they can do things 'right' or are emotionally attached to controlling every aspect of the business."
  },
  {
    question: "Why do founders struggle to delegate?",
    answer: "Founders struggle to delegate for several reasons: fear of losing control, belief that no one can do it as well as them, lack of trust in team members, inability to let go of tasks they enjoy, no documented processes to hand off, and difficulty transitioning from 'doer' to 'leader' mindset."
  },
  {
    question: "How do I know if I'm not delegating enough as a founder?",
    answer: "Warning signs include: being the only one who can approve expenses, product decisions waiting for your calendar, still writing production code, answering customer support tickets, team members CCing you on everything, having more than 8 direct reports, working weekends alone, and things breaking when you take time off."
  },
  {
    question: "What is the founder delegation framework?",
    answer: "The founder delegation framework has three phases: (1) Foundation (document processes, identify unique value, hire for ownership), (2) Transfer (delegate decisions not just tasks, set metrics not methods, create escalation paths), (3) Scale (build manager layer, move to weekly syncs, celebrate autonomous wins)."
  },
  {
    question: "How do I delegate effectively as a startup CEO?",
    answer: "Delegate effectively by: hiring people who've owned outcomes before, giving authority with responsibility, defining success metrics rather than methods, creating clear escalation thresholds, moving from daily check-ins to weekly accountability, and publicly celebrating when teams achieve outcomes without your involvement."
  }
];

// IT Stakeholder Roles FAQs
export const itStakeholderFAQs = [
  {
    question: "What is the difference between a CTO and a CIO?",
    answer: "The CTO (Chief Technology Officer) focuses on technology strategy, product innovation, and future technology direction. The CIO (Chief Information Officer) manages IT operations, infrastructure, cybersecurity, and aligns IT with business objectives. CTOs answer 'what should we build?' while CIOs answer 'how do we run efficiently?'"
  },
  {
    question: "What does an IT Director do compared to a CIO?",
    answer: "The IT Director executes the strategy set by the CIO, managing IT teams, daily operations, and technology projects. They handle budget management, vendor relationships, and resource allocation. The CIO sets policy and strategy; the IT Director implements it."
  },
  {
    question: "What is a RACI matrix in IT project management?",
    answer: "A RACI matrix defines four roles for each activity: Responsible (does the work), Accountable (final decision maker), Consulted (provides input), and Informed (kept updated). It prevents confusion about who owns decisions and who needs to be involved in IT projects."
  },
  {
    question: "Should a company have both a CTO and a CIO?",
    answer: "Large enterprises typically have both: CTO for product/technology innovation and CIO for IT operations/infrastructure. Smaller companies often combine these roles. If technology is your product, you need a CTO. If technology supports your business operations, you need a CIO."
  },
  {
    question: "What is the role of a DevOps Engineer in an organization?",
    answer: "DevOps Engineers bridge development and operations by building CI/CD pipelines, managing cloud infrastructure, ensuring system reliability, and automating deployments. They focus on delivery speed, system uptime, and infrastructure as code. They typically report to the IT Director or Engineering Manager."
  },
  {
    question: "How do Product Managers work with IT stakeholders?",
    answer: "Product Managers define what should be built based on user needs, then collaborate with CTOs on technical feasibility, Engineering Managers on implementation, and IT Directors on deployment. They prioritize features, document requirements, and measure product success."
  }
];

// Microsoft drops Claude Code FAQs
export const microsoftClaudeCodeFAQs = [
  {
    question: "Is Microsoft really dropping Claude Code?",
    answer: "Yes. On May 14, 2026, The Verge's Tom Warren reported that Microsoft is cancelling most internal Claude Code licenses across its Experiences + Devices group — which includes engineers for Windows, Microsoft 365, Outlook, Microsoft Teams, and Surface — by the end of June 2026. Engineers are being moved to GitHub Copilot CLI instead."
  },
  {
    question: "When exactly does Microsoft cut off Claude Code?",
    answer: "Microsoft's Experiences + Devices team is winding down Claude Code usage by the end of June 2026, which aligns with Microsoft's fiscal year end on June 30, 2026. Engineers were told to begin transitioning their workflows to GitHub Copilot CLI in the weeks ahead of the cutoff."
  },
  {
    question: "How many Microsoft engineers are affected?",
    answer: "Reporting from The Verge says 'thousands' of Microsoft developers in the Experiences + Devices organization will lose access. Secondary outlets (ABP Live, Pomegra, byteiota) put the figure at roughly 100,000 engineers across the affected divisions. Microsoft has not published an official headcount."
  },
  {
    question: "Why is Microsoft dropping Claude Code?",
    answer: "Officially, Microsoft told employees the decision is about converging on GitHub Copilot CLI as the main agentic command-line tool inside Experiences + Devices. Unofficially, sources told The Verge the decision is also financial — Claude Code's token-based usage scaled far past what Microsoft had budgeted in the six months since rollout in December 2025."
  },
  {
    question: "Did Microsoft really burn through its yearly AI budget in months?",
    answer: "Microsoft has not confirmed a specific number publicly. What is confirmed: (1) Claude Code became 'very popular' inside Microsoft in the six months after December 2025 rollout, (2) sources told The Verge the cancellation is partly a 'financial' decision, and (3) the cutoff lines up with Microsoft's June 30 fiscal year end. The broader 'burned through AI budget' framing is consistent with industry-wide reporting (e.g., Uber's president saying AI spend is 'harder to justify', also covered by The Verge in May 2026)."
  },
  {
    question: "What is GitHub Copilot CLI and why is Microsoft pushing it?",
    answer: "GitHub Copilot CLI is GitHub's command-line version of Copilot — an agentic coding tool that runs outside IDEs like Visual Studio Code. Microsoft sells Copilot to customers as its flagship AI coding product, so having its own engineers prefer Anthropic's Claude Code internally was both off-message and expensive. Consolidating on Copilot CLI lets Microsoft use its own infrastructure and absorb the cost internally instead of paying Anthropic per token."
  },
  {
    question: "Does this mean Claude Code is bad or losing to Copilot?",
    answer: "No. Multiple sources told The Verge that Claude Code was 'very popular' inside Microsoft and was actively undermining adoption of Copilot CLI. The decision is about Microsoft's business model and cost structure, not a quality judgment on Anthropic's product. Claude Code remains widely used outside Microsoft and is Anthropic's fastest-growing developer product."
  },
  {
    question: "What does this mean for companies paying per-token for AI coding tools?",
    answer: "It is the loudest signal yet that token-based AI coding costs can scale faster than anyone budgeted, even at Microsoft's scale. Practical takeaways: (1) measure per-engineer token cost monthly, not annually, (2) cap individual usage before procurement does it for you, (3) treat 'unlimited' AI seat pricing as more predictable than pay-per-token for large teams, (4) re-evaluate after any model upgrade — newer reasoning models can double or triple token spend overnight."
  }
];

// Helper to get FAQ schema for an insight page
export const getInsightFAQSchema = (slug: string) => {
  const faqs = getInsightFAQs(slug);
  return faqs.length > 0 ? createFAQSchema(faqs) : null;
};

// AI cost vs employees FAQs
export const aiCostVsEmployeesFAQs = [
  {
    question: "Is AI actually cheaper than hiring a human employee in 2026?",
    answer: "Usually not. A 2024 MIT CSAIL study (Svanberg, Li, Fleming, Goehring, Thompson — 'Beyond AI Exposure: Which Tasks are Cost-Effective to Automate with Computer Vision?') found AI is cost-effective for only about 23% of worker pay that is exposed to AI vision automation. For the remaining ~77%, a human is still the cheaper option once you account for build, deploy, and run costs. The math has tightened with frontier LLMs, but token-billed AI is rarely a flat win on cost alone."
  },
  {
    question: "Did Nvidia's own AI lead really say compute costs more than employees?",
    answer: "Yes. Bryan Catanzaro, Nvidia's VP of Applied Deep Learning Research, told Axios: 'For my team, the cost of compute is far beyond the costs of the employees.' Notable because Nvidia sells the GPUs that drive those compute bills. The quote was widely reported in April–May 2026 by Axios, Tom's Hardware, WCCFTech, IBTimes UK and FactCheckRadar."
  },
  {
    question: "Did Uber really burn its entire 2026 AI budget in four months?",
    answer: "Yes. Uber CTO Praveen Neppalli Naga said the company's full-year 2026 AI tooling budget was exhausted by April — about four months into the fiscal year — after Anthropic's Claude Code adoption exploded across the engineering org. Some engineers reportedly hit $500–$2,000/month in token spend per person. Reported by Business Tech News, Startup Fortune, Awesome Agents and BERI (May–June 2026)."
  },
  {
    question: "How much is Big Tech spending on AI in 2026?",
    answer: "Roughly $725–$740 billion in combined AI capital expenditure across the hyperscalers (Microsoft, Google, Meta, Amazon) — Morgan Stanley's published figure is $740B, Wedbush/Tom's Hardware reports $725B and a 77% YoY increase, CNBC reports the combined number is approaching $700B with free cash flow taking a major hit. The exact number varies by analyst, but the direction — a sharp step-up from 2025 — does not."
  },
  {
    question: "What's the most expensive AI runaway cost story on record?",
    answer: "Public examples include a Reddit developer who left Claude Code running overnight on a 30-minute update-check loop and woke up to a ~$6,000 bill (reported by MakeUseOf, 2026), and Microsoft's internal Claude Code program being shut down by June 30, 2026 after six months because token spend ran past forecast across ~100,000 engineers (The Verge, Tom Warren). The common thread: per-token billing turns 'left running by accident' into real money fast."
  },
  {
    question: "If AI is so expensive, why are companies still buying it?",
    answer: "Because cost-per-task is the wrong frame for the use cases where AI wins. AI's real edge is in (1) tasks where it is 10–100× faster than a human (research, drafting, code scaffolding), (2) work that didn't exist before (24/7 agents, instant translation, on-demand summarization), and (3) cases where it removes a hiring bottleneck. The mistake is treating AI as a 1:1 replacement for a salaried role across every job — MIT's data is clear that this works for only about 1 in 4 jobs today."
  },
  {
    question: "How should I budget for AI in 2026 without getting burned?",
    answer: "Treat AI like cloud spend, not like SaaS. Practical rules: (1) put a hard per-user monthly token cap before procurement does it for you, (2) model the worst-case month at 2–3× last month — reasoning models can double overnight, (3) prefer fixed-seat pricing (Copilot, Cursor Pro, Lovable) over raw pay-per-token for any team larger than ~10 people, (4) audit usage monthly, not annually, (5) measure value in tasks completed per dollar, not in API calls."
  },
  {
    question: "Will AI replace my employees by the end of 2026?",
    answer: "For most roles, no. The 2024 MIT CSAIL study explicitly modeled this and concluded that even with rapid cost declines, full automation of most vision-exposed tasks would take years — and only ~23% of exposed worker pay is cost-effective to automate today. The more realistic 2026 outcome is augmentation: smaller teams doing more, with AI handling repetitive sub-tasks. The companies that already laid off heavily on the assumption of pure replacement (Klarna, Salesforce, parts of IBM) have publicly walked back portions of that stance."
  }
];
